import { useQuery } from '@tanstack/react-query';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useParams } from 'react-router';

import { api, unwrap } from '../api/client.js';
import { HelpButton } from '../components/HelpButton.js';
import { PageTitle } from '../components/Layouts.js';
import { AlertIcon, CheckIcon, ClockIcon, XIcon } from '../components/icons.js';
import { GradeBadge, useLocale } from '../components/site.js';
import { Card, LoadError, Loading, Notice, SeverityBadge } from '../components/ui.js';
import { errorMessage } from '../errors.js';

type StepStatus = 'pending' | 'running' | 'done' | 'failed';

function StepIcon({ status }: { status: StepStatus }) {
  const icon: Record<StepStatus, ReactNode> = {
    pending: <ClockIcon />,
    running: <ClockIcon />,
    done: <CheckIcon />,
    failed: <XIcon />,
  };
  const color = {
    pending: 'text-slate-500',
    running: 'text-blue-800',
    done: 'text-green-800',
    failed: 'text-red-800',
  }[status];
  return <span className={`${color} shrink-0`}>{icon[status]}</span>;
}

/** US-02: the first checks of a new site run while the user watches, then grade and top 3. */
export function Onboarding() {
  const { siteId = '' } = useParams();
  const { t } = useTranslation();
  const locale = useLocale();
  const location = useLocation();
  const unreachable = (location.state as { unreachable?: boolean } | null)?.unreachable;
  const progress = useQuery({
    queryKey: ['site', siteId, 'onboarding'],
    queryFn: () => unwrap(api.GET('/sites/{siteId}/onboarding', { params: { path: { siteId } } })),
    refetchInterval: (query) => (query.state.data?.completed ? false : 2000),
  });
  const done = progress.data?.completed === true;
  const overview = useQuery({
    queryKey: ['site', siteId, 'overview', locale],
    queryFn: () =>
      unwrap(
        api.GET('/sites/{siteId}/overview', {
          params: { path: { siteId }, query: { locale } },
        }),
      ),
    enabled: done,
    // The score settles about a minute after the last check (debounce).
    refetchInterval: (query) => (query.state.data?.health ? false : 5000),
  });

  if (progress.isPending) return <Loading />;
  if (progress.isError)
    return (
      <LoadError
        message={errorMessage(progress.error, t)}
        onRetry={() => void progress.refetch()}
      />
    );
  const steps = progress.data.steps;
  const finished = steps.filter((s) => s.status === 'done' || s.status === 'failed').length;
  const health = overview.data?.health;

  return (
    <>
      <PageTitle>{t('onboarding.title')}</PageTitle>
      {unreachable ? (
        <div className="mb-4">
          <Notice kind="warning">{t('addSite.unreachable')}</Notice>
        </div>
      ) : null}
      <Card className="mb-6">
        <h2 className="mb-1 text-xl font-semibold">
          {done ? t('onboarding.finished') : t('onboarding.running')}
        </h2>
        <p className="mb-3 text-slate-700">
          {t('onboarding.progress', { done: finished, total: steps.length })}
        </p>
        <progress
          className="mb-4 h-3 w-full accent-blue-700"
          max={steps.length}
          value={finished}
          aria-label={t('onboarding.progressLabel')}
        />
        <ul className="flex flex-col gap-2" aria-live="polite">
          {steps.map((step) => (
            <li key={step.key} className="flex items-center gap-3">
              <StepIcon status={step.status} />
              <span className="font-medium">{t(`onboarding.steps.${step.key}`)}</span>
              <span className="text-sm text-slate-700">
                {t(`onboarding.status.${step.status}`)}
              </span>
            </li>
          ))}
        </ul>
      </Card>

      {done ? (
        <section aria-labelledby="result-title" className="flex flex-col gap-4">
          <h2 id="result-title" className="text-xl font-semibold">
            {t('onboarding.result')}
          </h2>
          {health ? (
            <Card>
              <div className="flex flex-wrap items-center gap-4">
                <GradeBadge grade={health.grade} score={health.score} />
                <p className="text-lg font-semibold">{health.label}</p>
              </div>
            </Card>
          ) : (
            <Notice kind="info">{t('onboarding.scoring')}</Notice>
          )}
          {overview.data ? (
            overview.data.topFindings.length === 0 ? (
              <Notice kind="success">{t('site.top.empty')}</Notice>
            ) : (
              <ol className="flex flex-col gap-2">
                {overview.data.topFindings.map((f) => (
                  <li
                    key={f.id}
                    className="flex flex-col gap-2 rounded-md border border-slate-200 bg-white px-4 py-3"
                  >
                    <span className="flex flex-wrap items-center gap-2">
                      <SeverityBadge severity={f.severity} />
                      <span className="font-medium">{f.title}</span>
                    </span>
                    {f.service ? (
                      <span>
                        <HelpButton
                          siteId={siteId}
                          service={f.service}
                          findingId={f.id}
                          ruleCode={f.ruleCode}
                        />
                      </span>
                    ) : null}
                  </li>
                ))}
              </ol>
            )
          ) : null}
          <p className="flex flex-wrap gap-4">
            <Link
              to={`/sites/${siteId}`}
              className="inline-flex min-h-11 items-center rounded-md bg-blue-700 px-4 font-semibold text-white hover:bg-blue-800"
            >
              {t('onboarding.toDashboard')}
            </Link>
            <Link
              to={`/sites/${siteId}/findings`}
              className="inline-flex min-h-11 items-center font-semibold text-blue-800 underline"
            >
              {t('onboarding.allFindings')}
            </Link>
          </p>
        </section>
      ) : (
        <p className="flex items-center gap-2 text-slate-700">
          <AlertIcon />
          {t('onboarding.canLeave')}
        </p>
      )}
    </>
  );
}
